import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { AppShell } from './shell/AppShell';
import { RequireHandleClaimed } from './shell/RequireHandleClaimed';
import { HabitatScreen } from './screens/HabitatScreen';
import { RegisterScreen } from './screens/RegisterScreen';
import { LoginScreen } from './screens/LoginScreen';
import { MeScreen } from './screens/MeScreen';
import { RouteErrorBoundary } from './screens/RouteErrorBoundary';
import { NotFoundScreen } from './screens/NotFoundScreen';
import { StaffGate } from './admin/StaffGate';

// RouteErrorBoundary as errorElement: a navigation that throws (eg. a lazy route chunk
// that never got precached by the SW, while offline) gets an honest full-screen state
// instead of React Router's default crash/blank error boundary (SCREENS.md, §11.11).
// It checks live connectivity itself — offline shows the "Reconnecting…" shell,
// online shows a generic "couldn't load this screen" retry card, so a genuine bug
// while online is never misreported as a connectivity problem.
export const routes: RouteObject[] = [
  { path: '/register', element: <RegisterScreen />, errorElement: <RouteErrorBoundary /> },
  { path: '/login', element: <LoginScreen />, errorElement: <RouteErrorBoundary /> },
  // Phase 6 Task 15 (Decision #10, SCREENS.md "/claim-handle — Choose your handle (FULL-SCREEN
  // route outside the shell)") — a top-level sibling of /register and /login, deliberately OUTSIDE
  // the RequireHandleClaimed-wrapped group below: that gate redirects HERE, so this route can
  // never live inside it (the gate would redirect to itself forever).
  {
    path: '/claim-handle',
    lazy: () => import('./screens/ClaimHandleScreen'),
    errorElement: <RouteErrorBoundary />,
  },
  // Task 15 review fix (IMPORTANT-1): every authenticated route — AppShell's own tabbed children
  // AND its full-screen siblings (wardrobe/battle/minigames) alike — is now wrapped in ONE shared
  // parent route using RequireHandleClaimed as its `element`. Previously the Decision #10 rename
  // gate lived only inside AppShell, so a direct deep link / restored back-history entry / typed
  // URL straight into /wardrobe/:companionId, /world/battle/:battleId, or
  // /world/minigames/:gameCode mounted the screen without ever hitting the gate — this single
  // router-level choke point closes that for good (see RequireHandleClaimed's own javadoc) and
  // can't be silently reopened by a future full-screen sibling the way a per-screen check could.
  {
    element: <RequireHandleClaimed />,
    errorElement: <RouteErrorBoundary />,
    children: [
      // The wardrobe USED to live here, as a router sibling of AppShell, because §11.2 says
      // "Full-screen modals only for the wardrobe and minigames". It is now an ordinary tab
      // inside the shell (both routes below) — an owner-authorised deviation from that line,
      // chosen from four costed options; see docs/RULINGS.md "Wardrobe as a shell tab" and
      // docs/demos/wardrobe-nav-demo.html. Battle and minigames are unchanged and remain
      // full-screen siblings.
      // Task 16: the battle screen (SCREENS.md "/world/battle/:battleId" — "a full-screen route
      // (like /wardrobe/:id), outside the app shell") — same sibling shape as /wardrobe above,
      // for the identical reason (no BottomNav mechanism to hide per-route, and this genuinely is
      // a full-screen takeover, not an ordinary tabbed sub-screen).
      {
        path: '/world/battle/:battleId',
        lazy: () => import('./battle/BattleScreen'),
        errorElement: <RouteErrorBoundary />,
      },
      // Phase 5 Task 10: the minigame screen (SCREENS.md "/world/minigames/:gameCode" — "a
      // full-screen lazy route outside the app shell, sibling of /world/battle/:battleId") — same
      // sibling shape as /wardrobe and /world/battle above, for the identical reason (a genuine
      // full-screen takeover with no BottomNav).
      {
        path: '/world/minigames/:gameCode',
        lazy: () => import('./minigames/MinigameScreen'),
        errorElement: <RouteErrorBoundary />,
      },
      // Task 14 (live PvP seasons, SCREENS.md "/world/arena/reports/:battleId — Duel report"): the
      // read-only duel replay — a full-screen sibling of `/world/battle/:battleId` above, for the
      // identical reason (the same stage-and-floor takeover, no tab chrome). Nesting is irrelevant
      // to matching: React Router ranks by specificity, so this never competes with the in-shell
      // `/world/arena` leaf further down.
      {
        path: '/world/arena/reports/:battleId',
        lazy: () => import('./arena/ReplayScreen'),
        errorElement: <RouteErrorBoundary />,
      },
      // Phase 7 Task 11 (SCREENS.md "/admin — Staff console group"): the WHOLE `/admin/**` route
      // group is a top-level sibling here, same shape as the wardrobe/battle/minigame full-screen
      // routes above — internal tool, no player tab chrome at all (R6), so it does NOT nest under
      // `<AppShell>`. `StaffGate` (not `RouteErrorBoundary`'s job) is the one gate that decides
      // whether any of these children ever render at all — see that component's own javadoc for
      // why a non-staff account gets the app's generic NotFoundScreen, never a hint that this
      // group exists.
      {
        element: <StaffGate />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { path: '/admin', lazy: () => import('./admin/AdminHomeScreen') },
          { path: '/admin/moderation', lazy: () => import('./admin/ModerationQueueScreen') },
          { path: '/admin/audit', lazy: () => import('./admin/AuditLogScreen') },
          { path: '/admin/activities', lazy: () => import('./admin/ActivitiesScreen') },
          { path: '/admin/notifications', lazy: () => import('./admin/NotificationDashboardScreen') },
          // Phase 7 Task 12: the last two of the Task 1 draft's seven /admin nav cards.
          { path: '/admin/economy', lazy: () => import('./admin/EconomyDashboardScreen') },
          { path: '/admin/analytics', lazy: () => import('./admin/AnalyticsDashboardScreen') },
        ],
      },
      {
        element: <AppShell />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { path: '/', element: <Navigate to="/habitat" replace /> },
          { path: '/habitat', element: <HabitatScreen /> },
          { path: '/companions/new', lazy: () => import('./companion/NewCompanionScreen') },
          // Task 18: World-lite (SCREENS.md "/world"), the Thatchwell shop, and Forage —
          // ordinary tabbed/sub screens inside AppShell (bottom nav visible, unlike the
          // wardrobe's full-screen modal above): the shop/forage routes are reached FROM /world,
          // not full-screen takeovers, so they keep the shell and get their own quiet
          // back link instead — `← World` for the shop/forage routes, and for the Training School
          // the land it was tapped from (`&land=`, see hotspotMeta.ts). The label always names the
          // route it actually goes to; docs/SCREENS.md "Sub-screen back link".
          { path: '/world', lazy: () => import('./world/WorldScreen') },
          { path: '/world/shops/:shopCode', lazy: () => import('./world/ShopScreen') },
          { path: '/world/forage/:activityCode', lazy: () => import('./world/ForageScreen') },
          { path: '/world/craft/:activityCode', lazy: () => import('./world/CraftScreen') },
          // Task 21: the Training School (SCREENS.md "/world/training") — an ordinary tabbed
          // sub-screen reached FROM /world (bottom nav visible), same convention as the
          // shop/forage routes above, not a full-screen modal.
          { path: '/world/training', lazy: () => import('./world/TrainingScreen') },
          // Task 17: the Arena (SCREENS.md "/world/arena") — registered unconditionally, same as
          // every other lazy child route here; ArenaScreen itself is the actual `arena_pvp` flag
          // gate (its own header comment) since React Router has no runtime-conditional route
          // registration.
          { path: '/world/arena', lazy: () => import('./arena/ArenaScreen') },
          // Task 11 (live battles and co-op, SCREENS.md "/world/coop/lobbies/:id — Lobby sheet"):
          // an in-shell bottom sheet (bottom nav visible), same convention as `/world/arena`
          // above — reached from `BattleVenueSheet`'s "Together" row (as the newly-created host)
          // or an inbox `InviteRow`'s Join tap (as an invited keeper), and must be a real,
          // deep-linkable route (not sheet-open state owned by some parent screen) since a guest
          // reaches it from a completely different screen than the host does.
          { path: '/world/coop/lobbies/:id', lazy: () => import('./coop/LobbySheet') },
          // Task 19 (§11.6): the land-map screen — a deep-link target (SCREENS.md "Global":
          // notification taps must render standalone, reachable without navigating there from
          // /world first), hence its own top-level child route rather than nesting under /world.
          { path: '/world/lands/:landCode', lazy: () => import('./world/LandScreen') },
          // Phase 7 Task 13 (SCREENS.md "/world/events/:eventCode — Event"): reached from the
          // World hub's own event ribbon, or a direct deep link — an ordinary tabbed sub-screen
          // (bottom nav visible), same convention as the land/shop/forage routes above, not a
          // full-screen modal.
          { path: '/world/events/:code', lazy: () => import('./world/EventScreen') },
          // Task 20: the Journal (SCREENS.md "/journal") — dailies/weeklies/streak/story, reached
          // from the World hub's own pinned summary card. An ordinary tabbed sub-screen (bottom
          // nav visible), same as the shop/forage/land routes above, not a full-screen modal.
          { path: '/journal', lazy: () => import('./journal/JournalScreen') },
          // Both wardrobe routes are shell children now. `/wardrobe` is the TAB and renders
          // the active companion's wardrobe directly — it is no longer a redirect, so tapping the
          // tab pages straight to content. `/wardrobe/:companionId` still serves deep links and
          // Habitat's own Dress action.
          { path: '/wardrobe', lazy: () => import('./wardrobe/WardrobeScreen') },
          { path: '/wardrobe/:companionId', lazy: () => import('./wardrobe/WardrobeScreen') },
          // Task 16: the Bag tab (SCREENS.md "/bag") — an ordinary tabbed screen inside AppShell
          // (bottom nav visible), unlike the wardrobe's full-screen modal above, so it stays a
          // child route here rather than a top-level sibling.
          { path: '/bag', lazy: () => import('./bag/BagScreen') },
          { path: '/me', element: <MeScreen /> },
          // Task 17: Keeper creator, public profiles, Look gallery — ordinary tabbed screens
          // inside the authed shell (bottom nav visible, unlike the wardrobe's full-screen modal
          // above), per SCREENS.md.
          { path: '/me/keeper', lazy: () => import('./keeper/KeeperCreatorScreen') },
          { path: '/profile/:displayName', lazy: () => import('./profile/ProfileScreen') },
          { path: '/looks/gallery', lazy: () => import('./looks/LookGalleryScreen') },
          // Task 16: Friends and the wishlist editor (SCREENS.md "/me/friends"/"/me/wishlist") —
          // two of the Me-hub Social section's four links (Task 15); `/guild` stays dangling
          // (Task 19's own route). Same ordinary tabbed-screen convention as every other /me/*
          // route above.
          { path: '/me/friends', lazy: () => import('./friends/FriendsScreen') },
          { path: '/me/wishlist', lazy: () => import('./wishlist/WishlistScreen') },
          // Task 17: chat, both tiers (SCREENS.md "/me/chat"/"/me/chat/:conversationId",
          // Decision #9) — the Me-hub Social section's last remaining dangling link. The thread
          // is its own lazy route (resolves the brief's own "lazy route?" question as yes),
          // same convention as every other /me/* route above.
          { path: '/me/chat', lazy: () => import('./chat/ConversationsScreen') },
          { path: '/me/chat/:conversationId', lazy: () => import('./chat/ChatThreadScreen') },
          // Task 18 (SCREENS.md "/trades"/"/trades/:tradeId"): direct player trades. Ordinary
          // tabbed sub-screens inside the authed shell (bottom nav visible), same convention as
          // every other non-full-screen route above — a trade is reached FROM a friend's profile
          // or a bag item's detail sheet, never a full-screen takeover of its own.
          { path: '/trades', lazy: () => import('./trades/TradesScreen') },
          { path: '/trades/:tradeId', lazy: () => import('./trades/TradeScreen') },
          // Task 18 (SCREENS.md "/world/auction — The Gearloch Exchange"): reached from
          // Harborloop's `cinderport_gearloch_exchange` hotspot (`hotspotMeta.ts`'s new
          // `auction_kiosk` case) — an in-shell lazy route, same convention as
          // `/world/shops/:shopCode`/`/world/training` above, not a full-screen modal.
          { path: '/world/auction', lazy: () => import('./world/AuctionScreen') },
          // Task 19 (SCREENS.md "/guild — Guild home"): the last dangling Me-hub Social link
          // (Task 15's own flagged gap). `/guild` resolves to the home tabs (in a guild) or
          // redirects to `/guild/directory` (guildless) — see GuildScreen's own header comment.
          // `/guild/directory` is its own route (not just a redirect target) so a guild member can
          // still reach the browse list directly (GuildHomeScreen's "Browse guilds" link).
          { path: '/guild', lazy: () => import('./guilds/GuildScreen') },
          { path: '/guild/directory', lazy: () => import('./guilds/GuildDirectoryScreen') },
          // Task 19 (SCREENS.md "/world/stalls — Market Stalls"): reached from Thatchwell's new
          // `market_stalls` hotspot (`hotspotMeta.ts`'s new case) — in-shell lazy routes, same
          // convention as `/world/shops/:shopCode`/`/world/auction` above, not full-screen modals.
          { path: '/world/stalls', lazy: () => import('./stalls/StallDirectoryScreen') },
          { path: '/world/stalls/:code', lazy: () => import('./stalls/StallScreen') },
          // Task 22 (SCREENS.md "/me/badges"/"/me/collections", §8.7 "visible progress
          // everywhere") — ordinary tabbed sub-screens inside the authed shell (bottom nav
          // visible), same convention as every other /me/* route above.
          { path: '/me/badges', lazy: () => import('./badges/BadgesScreen') },
          { path: '/me/collections', lazy: () => import('./collections/CollectionsScreen') },
          // Task 23 (SCREENS.md "/me/notifications", §11.11) — same convention as every other
          // /me/* route above.
          { path: '/me/notifications', lazy: () => import('./notifications/NotificationSettingsScreen') },
          // Anything unmatched: an honest "doesn't exist" INSIDE the shell (nav stays) instead of the
          // router's error screen, whose "Try again" just reloaded the same missing path.
          { path: '*', element: <NotFoundScreen /> },
        ],
      },
    ],
  },
  // Fix round (review finding, IMPORTANT): the trailing `/*` (not just `/dev/gallery`) matters —
  // several gallery entries (gallery-entries.tsx's ProfileScreen/ShopScreen/ForageScreen/LoginScreen
  // states) simulate a specific route for their own useParams()/useLocation().state via a nested
  // `<Routes location={...}>`, which react-router only permits when the location is a descendant
  // of the PARENT route's own matched pathname base — which react-router only extends past the
  // literal path text when that path itself ends in `/*` (see react-router's own "Please change
  // the parent <Route path=...> to <Route path=.../*>" invariant message). Without the splat here,
  // any such nested override throws.
  ...(import.meta.env.DEV
    ? [
        { path: '/dev/gallery/*', lazy: () => import('./gallery/GalleryRoute') },
        // 3D battle spec §6.2: the enemy models beside their PNGs (the owner review gate).
        { path: '/dev/enemies3d', lazy: () => import('./gallery/Enemies3DRoute') },
        // wardrobe spec §7 (W9): a dressed pet gored, its clothes cut as cloth (the hardcore-mode seam).
        { path: '/dev/pets3d', lazy: () => import('./gallery/Pets3DRoute') },
      ]
    : []),
];
export const router = () => createBrowserRouter(routes);
